import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Loader } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faMicrophone, 
  faCalendarAlt, 
  faClock,
  faEllipsisVertical,
  faTrash,
  faDownload,
  faVideo,
  faUpload
} from '@fortawesome/free-solid-svg-icons';
import { Project } from '../../../services/projectService';
import { getApiUrl } from '../../../config/api';
import styles from '../../CreateContentLandingPage/CreateContent.module.css';

interface ProjectsListProps {
  projects: Project[];
  isLoading: boolean;
  error: string | null;
  searchTerm: string;
  selectedOption: 'all' | 'recent';
  activeActionDropdown: string | null;
  onSearchChange: (term: string) => void;
  onFilterChange: (option: 'all' | 'recent') => void;
  onToggleDropdown: (projectId: string) => void;
  onContinueProject: (project: Project) => void;
  onDeleteProject: (projectId: string) => void;
  onDuplicateProject: (projectId: string) => void;
  onUploadClick: () => void;
}

const ProjectsList: React.FC<ProjectsListProps> = ({
  projects,
  isLoading,
  error,
  searchTerm,
  selectedOption,
  activeActionDropdown,
  onSearchChange,
  onFilterChange,
  onToggleDropdown,
  onContinueProject,
  onDeleteProject,
  onDuplicateProject,
  onUploadClick
}) => {
  const { t } = useTranslation('captionLandingPage');

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const filteredProjects = projects.filter(project => {
    if (!project.hasCaptions) return false;
    
    const hasVideoContent = project.originalVideoUrl || 
                           project.captionedVideoUrl || 
                           project.generatedVideoUrl;
    if (!hasVideoContent) return false;    
    
    if (selectedOption === 'recent') {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const projectDate = new Date(project.date);
      if (projectDate < sevenDaysAgo) return false;
    }
    
    if (searchTerm && !project.title.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    
    return true;
  });

  if (isLoading) {
    return (
      <div className={styles.projectsSection}>
        <div className={styles.loadingContainer}>
          <Loader size="xl" />
          <p>{t('projectsList.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.projectsSection}>
        <div className={styles.errorContainer}>
          <p className={styles.errorMessage}>{error}</p>
          <Button onClick={() => window.location.reload()}>
            {t('errors.tryAgain')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.projectsSection}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>{t('projectsList.title')}</h2>
        <div className={styles.searchAndFilter}>
          <div className={styles.searchBar}>
            <input
              type="text"
              placeholder={t('projectsList.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className={styles.searchInput}
            />
          </div>
          
          <div className={styles.filterOptions}>
            <button 
              className={`${styles.filterButton} ${selectedOption === 'all' ? styles.active : ''}`}
              onClick={() => onFilterChange('all')}
            >
              {t('projectsList.filters.all')} ({projects.length})
            </button>
            <button 
              className={`${styles.filterButton} ${selectedOption === 'recent' ? styles.active : ''}`}
              onClick={() => onFilterChange('recent')}
            >
              {t('projectsList.filters.recent')}
            </button>
          </div>
        </div>
      </div>
      
      {filteredProjects.length > 0 ? (
        <div className={styles.projectGrid}>
          {filteredProjects.map(project => (
            <div 
              key={project.id} 
              className={`${styles.projectCard} 
                ${activeActionDropdown === project.id ? styles.cardWithActiveDropdown : ''}
                ${project.captionedVideoUrl ? styles.hasGeneratedVideo : ''}
              `}
            >
              {project.captionedVideoUrl && (
                <a 
                  href={project.captionedVideoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className={styles.videoAvailableBadge}
                  onClick={(e) => e.stopPropagation()}
                  style={{ zIndex: 200 }}
                >
                  <FontAwesomeIcon icon={faDownload} />
                </a>
              )}
              <div className={styles.projectThumbnail}>
                {/* Use video preview for projects with video URLs */}
                {(project.generatedVideoUrl || project.originalVideoUrl || project.captionedVideoUrl) ? (
                  <video 
                    src={project.generatedVideoUrl || project.originalVideoUrl || project.captionedVideoUrl}
                    className={styles.videoPreview}
                    muted
                    preload="metadata"
                    onError={(e) => {
                      // Fallback to thumbnail image if video fails to load
                      const videoElement = e.target as HTMLVideoElement;
                      const img = document.createElement('img');
                      img.src = project.thumbnail || 'https://aiapptest2.s3.us-east-1.amazonaws.com/projects/library/images/1741139292210-aerial-view-of-wooden-bridge-over-the-river-fores-2023-11-27-05-11-54-utc.jpg';
                      img.alt = project.title;
                      img.className = styles.videoPreview;
                      img.onerror = () => {
                        img.src = 'https://aiapptest2.s3.us-east-1.amazonaws.com/projects/library/images/1741139292210-aerial-view-of-wooden-bridge-over-the-river-fores-2023-11-27-05-11-54-utc.jpg';
                      };
                      videoElement.parentNode?.replaceChild(img, videoElement);
                    }}
                  />
                ) : (
                  <img 
                    src={project.thumbnail} 
                    alt={project.title}
                    className={styles.videoPreview}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      if (project.type === 'audio') {
                        target.src = getApiUrl('/public/assets/audio-waveform.svg');
                      } else {
                        target.src = 'https://aiapptest2.s3.us-east-1.amazonaws.com/projects/library/images/1741139292210-aerial-view-of-wooden-bridge-over-the-river-fores-2023-11-27-05-11-54-utc.jpg';
                      }
                    }} 
                  />
                )}
                <div className={styles.projectType}>
                  <FontAwesomeIcon icon={project.type === 'audio' ? faMicrophone : faVideo} />
                </div>
                <div className={styles.projectDuration}>
                  <FontAwesomeIcon icon={faClock} />
                  <span>{project.duration}</span>
                </div>
              </div>
              
              <div className={styles.projectInfo}>
                <h3 className={styles.projectTitle}>{project.title}</h3>
                <div className={styles.projectMeta}>
                  <span className={styles.projectDate}>
                    <FontAwesomeIcon icon={faCalendarAlt} />
                    {formatDate(project.date)}
                  </span>
                </div>
              </div>
              
              <div className={styles.projectActions}>
                <Button 
                  className={styles.continueButton}
                  onClick={() => onContinueProject(project)}
                >
                  {t('projectCard.continue')}
                </Button>
                <div className={styles.moreActions}>
                  <button 
                    className={styles.moreButton}
                    onClick={() => onToggleDropdown(project.id)}
                  >
                    <FontAwesomeIcon icon={faEllipsisVertical} />
                  </button>
                  {activeActionDropdown === project.id && (
                    <div className={`${styles.actionDropdown} ${activeActionDropdown === project.id ? styles.active : ''}`}
                         onClick={(e) => e.stopPropagation()}
                    >
                      {project.captionedVideoUrl && (
                        <a 
                          className={styles.actionButton}
                          href={project.captionedVideoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                        >
                          <FontAwesomeIcon icon={faDownload} /> Download Video
                        </a>
                      )}
                      <button 
                        className={styles.actionButton}
                        onClick={() => onDuplicateProject(project.id)}
                      >
                        {t('projectCard.duplicate')}
                      </button>
                      <button 
                        className={styles.actionButton}
                        onClick={() => onDeleteProject(project.id)}
                      >
                        <FontAwesomeIcon icon={faTrash} /> {t('projectCard.delete')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.noProjects}>
          <FontAwesomeIcon icon={faPlus} size="2x" className={styles.noProjectsIcon} />
          <h3>No captioned videos found</h3>
          <p>Start by uploading a video or entering a URL to generate captions.</p>
          <Button 
            className={styles.createNewButton}
            onClick={onUploadClick}
            leftSection={<FontAwesomeIcon icon={faUpload} />}
          >
            Upload Video
          </Button>
        </div>
      )}
    </div>
  );
};

export default ProjectsList;
